import {compareAPPVersion, generateUserAgentValues} from '@financial/app-js-interfaces'
import {isAxiosError} from 'axios'

import {sendNeloToBFF} from '$services/nelo'
import {NeloLogLevel} from '$share/constants/nelo'
import {isSSR} from '$utils/common'
import {getRedirectConnectPageUrl, isUserAgreementNotFoundOrRevokeError} from '$utils/mydata'

import {getRedirectUrlFromAuthError, isAuthError} from './auth'
import {
    getRedirectPayAppAuthError,
    getRedirectPayappUrlFromMfaError,
    getRedirectUrlFromMfaError,
    isMfaSessionError,
    isPayAppOneDeviceInvalidError,
} from './mfa'

import type {QueryClientConfig} from '@tanstack/react-query'
import type {IncomingMessage} from 'http'

const handleThrowOnError = (error: Error) => {
    if (isSSR()) {
        return true // withGetServerSideProps에서 에러를 캐치해서 사용하기 위해서 true 로 설정
    }

    if (isAxiosError(error)) {
        let redirectUrl: string | undefined = ''
        const currentUrl = window.location.href
        const {isMobile, isPayApp = false, appVersion = ''} = generateUserAgentValues(window.navigator.userAgent)

        if (isAuthError(error)) {
            redirectUrl = getRedirectUrlFromAuthError(error, currentUrl, isMobile)
        } else if (isPayAppOneDeviceInvalidError(error)) {
            /**
             * payAppOneDeviceCheck 에러
             */
            redirectUrl = getRedirectPayAppAuthError(error)
        } else if (isMfaSessionError(error)) {
            const isSupportPayapp = isPayApp && appVersion && compareAPPVersion('2.1.0', '<=', appVersion)

            redirectUrl = isSupportPayapp
                ? getRedirectPayappUrlFromMfaError(error) || ''
                : getRedirectUrlFromMfaError(error) || ''
        } else if (isUserAgreementNotFoundOrRevokeError(error)) {
            redirectUrl = getRedirectConnectPageUrl(currentUrl)
        }

        if (redirectUrl) {
            window.location.href = redirectUrl
            return false
        }

        return false
    }

    return true
}

const queryClientConfig: (req?: IncomingMessage) => QueryClientConfig = (req) => {
    return {
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                refetchOnMount: false,
                staleTime: Infinity,
                retry: false,
                retryOnMount: false,
                throwOnError: handleThrowOnError,
            },
            mutations: {
                onError: (error: unknown) => {
                    if (error instanceof Error && !isAxiosError(error)) {
                        sendNeloToBFF(
                            {
                                logLevel: NeloLogLevel.Error,
                                logType: 'react-query-mutation',
                                log: {
                                    name: error.name,
                                    message: error.message,
                                    pageUrl: isSSR() ? req?.url : window.location.href,
                                    ...(error?.stack ? {stack: error.stack} : {}),
                                },
                            },
                            req,
                        )
                    }
                },
            },
        },
    }
}

export default queryClientConfig
